import { ComponentProps, useMemo } from 'react'

import { F0Box, F0Dialog } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import {
  EngagementRemindSurveyRespondentsDocument,
  RemindRespondentsDialogDataDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  surveyId: string
  formId: string
  onCancel?: () => void
  onSuccess?: () => void
}

export function RemindRespondentsDialog({ surveyId, formId, onCancel, onSuccess }: Props) {
  const { data, loading } = useQuery(RemindRespondentsDialogDataDocument, {
    variables: { surveyId, formId },
    fetchPolicy: 'cache-and-network',
  })

  const [notifySurvey] = useMutation(EngagementRemindSurveyRespondentsDocument, {
    extractorFn: (mutationData) => mutationData.surveysMutations.notifySurveysSurvey,
  })

  const form = data?.surveys.survey?.form
  const pendingConnection = data?.forms.pending
  const answeredCount = data?.forms.answered.totalCount ?? 0
  const totalCount = data?.forms.total.totalCount ?? 0

  const pendingAvatarList = useMemo(() => {
    if (!pendingConnection?.nodes?.length) return undefined

    const avatars = pendingConnection.nodes
      .filter((node): node is typeof node & { respondent: NonNullable<typeof node.respondent> } =>
        Boolean(node.respondent)
      )
      .map((node) => ({
        firstName: node.respondent.firstName,
        lastName: node.respondent.lastName,
        src: buildUrl(node.respondent.avatar),
      }))

    const pendingTotal = pendingConnection.totalCount ?? 0
    let remainingCount: number | undefined = Math.max(pendingTotal - avatars.length, 0)
    remainingCount = remainingCount === 0 ? undefined : remainingCount

    return { avatars, size: 'sm' as const, remainingCount, type: 'person' as const }
  }, [pendingConnection])

  const deadlineLabel = useMemo(() => {
    if (!form) return null

    const materializedForm = form.materializedForms[0]
    const daysToAnswer = form.configuration?.daysToAnswer
    let endDate: Date | null = null

    if (materializedForm?.startsAt && daysToAnswer) {
      const startDate = new Date(materializedForm.startsAt)
      endDate = new Date(startDate)
      endDate.setDate(endDate.getDate() + daysToAnswer)
    } else if (materializedForm?.endsAt) {
      endDate = new Date(materializedForm.endsAt)
    } else if (form.endsAt) {
      endDate = new Date(form.endsAt)
    }

    if (!endDate) return null

    const diffTime = endDate.getTime() - new Date().getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays < 0) {
      return i18n.t('surveys.participation_card.finished')
    }

    return i18n.tp('surveys.participation_card.days_left', { count: diffDays })
  }, [form])

  const shouldHideAnonymousCounts = form?.anonymous === true && answeredCount < 3

  const details: ComponentProps<typeof DetailsItemsList>['details'] = useMemo(() => {
    const items: ComponentProps<typeof DetailsItemsList>['details'] = [
      {
        title: i18n.t('surveys.remind_respondents_dialog.survey_name'),
        content: { text: form?.title ?? '', type: 'item' as const },
      },
    ]

    if (!shouldHideAnonymousCounts) {
      items.push(
        {
          title: i18n.t('surveys.remind_respondents_dialog.total_participants'),
          content: { text: totalCount.toString(), type: 'item' as const },
        },
        {
          title: i18n.t('surveys.remind_respondents_dialog.completed'),
          content: { text: answeredCount.toString(), type: 'item' as const },
        }
      )
    }

    if (shouldHideAnonymousCounts) {
      items.push({
        title: i18n.t('surveys.remind_respondents_dialog.pending'),
        content: {
          text: i18n.t('surveys.remind_respondents_dialog.anonymous_survey'),
          type: 'item' as const,
        },
      })
    } else if (pendingAvatarList && !form?.anonymous) {
      items.push({
        title: i18n.t('surveys.remind_respondents_dialog.pending'),
        content: { avatarList: pendingAvatarList, type: 'avatar-list' as const },
      })
    } else {
      items.push({
        title: i18n.t('surveys.remind_respondents_dialog.pending'),
        content: {
          text: (pendingConnection?.totalCount ?? 0).toString(),
          type: 'item' as const,
        },
      })
    }

    if (deadlineLabel) {
      items.push({
        title: i18n.t('surveys.remind_respondents_dialog.deadline'),
        content: { text: deadlineLabel, color: 'yellow' as const, type: 'dot-tag' as const },
      })
    }

    return items
  }, [
    form,
    totalCount,
    answeredCount,
    pendingAvatarList,
    pendingConnection,
    deadlineLabel,
    shouldHideAnonymousCounts,
  ])

  const handleSendReminder = async () => {
    const response = await notifySurvey({
      variables: { surveyId },
    })

    const handled = handleResult(response, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('surveys.remind_respondents_dialog.success_message'),
        time: 1000,
      })
      onSuccess?.()
    }
  }

  return (
    <F0Dialog
      isOpen
      onClose={onCancel}
      title={i18n.t('surveys.remind_respondents_dialog.title')}
      description={i18n.t('surveys.remind_respondents_dialog.description')}
      primaryAction={{
        label: i18n.t('surveys.remind_respondents_dialog.send_reminder'),
        onClick: handleSendReminder,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: () => onCancel?.(),
      }}
    >
      {loading ? (
        <F0Box padding="xl">
          <Loading />
        </F0Box>
      ) : (
        <DetailsItemsList details={details} tableView />
      )}
    </F0Dialog>
  )
}
